/**
 * Tracks the last role-specific shell the user was inside, so that the next
 * sign-in returns them there instead of always sending them to the role
 * stored on their account at registration time.
 *
 * The role string is persisted to localStorage (key: `lastActiveRole`) and
 * survives logout. We also remember the last sub-path inside that shell
 * (key: `lastActiveRolePath`) so we can return the user closer to where
 * they left off.
 */

const ROLE_KEY = 'lastActiveRole';
const PATH_KEY = 'lastActiveRolePath';

// Map a pathname → the conceptual role that owns it.
// `staff_admin` and `staff_regular` both map to the user.role === 'staff'
// backend state. We keep them as separate landing keys to choose the right
// landing page (admin vs regular member home).
export function detectRoleFromPath(pathname) {
  if (!pathname) return null;
  if (pathname.startsWith('/guest/')) return 'guest';
  if (pathname.startsWith('/host/')) return 'host';
  if (pathname.startsWith('/venue/onboarding') || pathname === '/venue/intro') return null;
  if (pathname.startsWith('/venue/')) return 'venue_owner';
  if (pathname.startsWith('/staff-admin/')) return 'staff_admin';
  if (pathname.startsWith('/staff-app/')) return 'staff_regular';
  return null;
}

export function rememberActiveRoleFromPath(pathname) {
  const role = detectRoleFromPath(pathname);
  if (!role) return;
  try {
    localStorage.setItem(ROLE_KEY, role);
    localStorage.setItem(PATH_KEY, pathname);
  } catch { /* localStorage might be unavailable in some WebViews */ }
}

export function rememberActiveRole(role) {
  if (!role) return;
  try { localStorage.setItem(ROLE_KEY, role); } catch { /* ignore */ }
}

export function getLastActiveRole() {
  try { return localStorage.getItem(ROLE_KEY) || ''; } catch { return ''; }
}

export function getLastActiveRolePath() {
  try { return localStorage.getItem(PATH_KEY) || ''; } catch { return ''; }
}

export function clearLastActiveRole() {
  try {
    localStorage.removeItem(ROLE_KEY);
    localStorage.removeItem(PATH_KEY);
  } catch { /* ignore */ }
}

/**
 * Given a `lastActiveRole` token and the freshly-logged-in `user` object,
 * return the default landing path inside that role IF the user still has
 * access to it. Returns null when the remembered role is no longer valid
 * (e.g. their host application was rejected since last login).
 */
export function getLandingPathForRememberedRole(lastRole, user, _lastPath) {
  // NOTE: the previous sub-path is deliberately IGNORED. On cold start
  // (app relaunch) users expect to land on the role's HOME / middle page
  // — not on whatever deep screen they happened to close the app on.
  // The `_lastPath` parameter is preserved for API stability with older
  // callers but has no effect on routing.
  if (!lastRole || !user) return null;

  const available = new Set(user.available_roles || []);
  const userRole = user.role;

  switch (lastRole) {
    case 'guest':
      // The /guest/* routes are gated by ProtectedRoute(requiredRole="guest"),
      // which only lets role='guest' or role='admin' through. `available_roles`
      // ALWAYS contains 'guest' on the backend, so we can't check availability
      // here — that would let host/venue_owner/staff users inherit a
      // remembered /guest/… path and loop with /login (Feb 2026 blank-screen
      // repro). Mirror ProtectedRoute's allow-list exactly: role only.
      if (userRole === 'guest' || userRole === 'admin') {
        return '/guest/home';
      }
      return null;
    case 'host':
      if (available.has('host') || userRole === 'host') {
        return '/host/active-qr';
      }
      return null;
    case 'venue_owner':
      // Venue owners land on the MIDDLE scan page — that's the "home" of
      // the venue app for day-to-day operations. Approved status required
      // to avoid dumping non-live venues onto scan (their scan won't work).
      if ((available.has('venue_owner') || userRole === 'venue_owner') && user.venue_status === 'approved') {
        return '/venue/scan';
      }
      return null;
    case 'staff_admin':
      if (userRole === 'staff' && (user.staff_role === 'admin' || available.has('venue_member'))) {
        return '/staff-admin/scan';
      }
      return null;
    case 'staff_regular':
      if (userRole === 'staff' || available.has('venue_member')) {
        return '/staff-app/scan';
      }
      return null;
    default:
      return null;
  }
}

/**
 * Return the backend `target_role` value matching a `lastActiveRole` token,
 * so the login handler can sync the backend role with the frontend's
 * remembered shell.
 */
export function getBackendTargetRoleForRememberedRole(lastRole) {
  switch (lastRole) {
    case 'guest': return 'guest';
    case 'host': return 'host';
    case 'venue_owner': return 'venue_owner';
    case 'staff_admin':
    case 'staff_regular':
      return 'venue_member';
    default:
      return null;
  }
}
